import { ApiWrapper, BaseMessages, ValueOf } from '../../types';
import { JBaseApi } from '../base';
import { OF } from '../../util/network';
import {
    ApplicationType,
    StudioApplication,
    StudioApplicationBase,
    StudioApplicationStatus,
    StudioApplicationStatusEnumBased,
} from './types';

export class JApplicationApi extends JBaseApi {
    /**
     * List applications
     */
    public list() {
        return this.network.post<ApplicationType[]>({ o: 'applications', f: 'list' });
    }

    /*******/

    /**
     * List applications
     * @returns  {@link JApplication} objects, in light format
     */
    public studioList() {
        return this.network.post<Array<StudioApplicationBase>>({ o: 'studio', f: 'list' });
    }

    /**
     * Get one application
     * @returns  {@link JApplication} object in full format
     */
    public studioGet(idApp: string, status?: StudioApplicationStatusEnumBased) {
        return this.network.post<StudioApplication>({ o: 'studio', f: 'get', idApp, status } as OF);
    }

    /**
     * Install this application.
     * If everything is ok, remove previous saved and autosaved versions of this application.
     * @param xmlBody: string XML file
     * @returns  {@link JApplication} object in full format
     */
    public studioInstall(xmlBody: string) {
        return this.network.postCSRF<StudioApplication>({ o: 'studio', f: 'install', value: xmlBody } as OF);
    }

    /**
     * Suspend an installed application.
     * @param idApp: application's id
     * @returns  {@link JApplication} object in full format
     */
    public studioSuspend(idApp: string) {
        return this.network.postCSRF<StudioApplication>({ o: 'studio', f: 'suspend', idApp } as OF);
    }

    /**
     * Restart an suspended application.
     * @param idApp: application's id
     * @returns  {@link JApplication} object in full format
     */
    public studioRestart(idApp: string) {
        return this.network.postCSRF<StudioApplication>({ o: 'studio', f: 'restart', idApp } as OF);
    }

    /**
     * Save a 'local', 'saved' or 'autosaved' version of the application
     * Returns a 'save' or 'autosaved' application.
     * @param idApp: application's id
     * @param value: data to be saved
     * @param status:  must be either 'saved' or 'autosaved'.
     * @returns  {@link JApplication} object in full format
     */
    public studioSave(
        idApp: string,
        value: string,
        status: ValueOf<StudioApplicationStatus.saved> | ValueOf<StudioApplicationStatus.autosaved>,
    ) {
        return this.network.postCSRF<StudioApplication>({
            o: 'studio',
            f: 'save',
            idApp,
            value,
            status,
        } as OF);
    }

    /**
     * Delete a selected status of the application
     * @param idApp application id to delete
     * @param status application status to select
     * @returns message after delete execution
     */
    public async studioDelete(idApp: string, status: StudioApplicationStatusEnumBased): Promise<BaseMessages> {
        return this.network
            .postCSRF<string>({
                o: 'studio',
                f: 'delete',
                idApp,
                status,
            } as OF)
            .then((res) => {
                return res.messages;
            });
    }

    /**
     * Get cssClass possible values.
     */
    public studioCssClassList(): Promise<ApiWrapper<Array<string>>> {
        return this.network.post<Array<string>>({ o: 'studio', f: 'icons' });
    }
}
